/**
 * ClientContext — Contexto global do cliente autenticado.
 *
 * Elimina prop drilling de `T` (tema), `dark/setDark` e dados básicos
 * do cliente para todos os componentes da árvore ClientLayout.
 */
import { createContext, useContext, type ReactNode } from "react";
import type { ThemeObject } from "./shared/theme.js";

export interface ClientContextValue {
  // Tema
  T: ThemeObject;
  dark: boolean;
  setDark: (dark: boolean) => void;

  // Navegação entre páginas
  setPage: (page: string) => void;

  // Dados do cliente
  clientId: string | null;
  clientName: string | null;
  profile: unknown;

  // Dados do snapshot atual (para evitar prop drilling de `data`)
  snapshotData: unknown;
  snapshotLoading: boolean;
  snapHistory: unknown[];

  // Dados processados (snapshotData || DATA_EMPTY) — consumido pelas páginas
  data: unknown;
  hasSnapshot: boolean;

  // Objeto `client` consolidado (patrimonio, nome, etc.)
  client: unknown;

  // Suitability
  suitStatus: string | null;
  clientSuitData: unknown;

  // Notificações
  notifications: unknown[];

  // Sprint 3: metadados de assessoria (clients_meta)
  clientMetaRow: unknown;
  upsertClientMeta: ((meta: Record<string, unknown>) => Promise<void>) | null;

  // Callbacks
  setShowEditSuit: (show: boolean) => void;
  onSnapshotCreated: () => void;

  // Privacy mode — masks financial values with asterisks
  privacyMode: boolean;
  setPrivacyMode: (mode: boolean) => void;
}

// ─── Tema padrão (LIGHT) — evita crash se Provider não estiver presente ──────
const LIGHT_FALLBACK = {
  navy: "#0B2447",
  navy2: "#19376D",
  gold: "#C9A227",
  bg: "#EEF2F7",
  card: "#FFFFFF",
  text: "#1E293B",
  sub: "#475569",
  border: "#E2E8F0",
  muted: "#94A3B8",
  row1: "#FFFFFF",
  row2: "#F8FAFC",
} as ThemeObject;

const ClientContext = createContext<ClientContextValue>({
  T: LIGHT_FALLBACK,
  dark: false,
  setDark: () => {},
  setPage: () => {},
  clientId: null,
  clientName: null,
  profile: null,
  snapshotData: null,
  snapshotLoading: true,
  snapHistory: [],
  data: null,
  hasSnapshot: false,
  client: null,
  suitStatus: null,
  clientSuitData: null,
  notifications: [],
  clientMetaRow: null,
  upsertClientMeta: null,
  setShowEditSuit: () => {},
  onSnapshotCreated: () => {},
  privacyMode: false,
  setPrivacyMode: () => {},
});

interface ClientProviderProps {
  value: ClientContextValue;
  children: ReactNode;
}

/**
 * Provider — envolva ClientLayout (ou AppInner) com este componente.
 */
export function ClientProvider({ value, children }: ClientProviderProps) {
  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
}

/**
 * Hook de consumo do contexto.
 * Lança erro informativo se usado fora do Provider.
 */
export function useClientContext(): ClientContextValue {
  const ctx = useContext(ClientContext);
  if (ctx === undefined) {
    throw new Error(
      "[LPA] useClientContext() deve ser chamado dentro de <ClientProvider>. " +
        "Verifique se o componente está dentro da árvore de ClientLayout."
    );
  }
  return ctx;
}

export default ClientContext;
